
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator'
import * as T from '@/types'
import * as C from '@/constants'
import config from '@/config'
import S from '@/store'
import _isEmpty from 'lodash/isEmpty'

import EntityViewMixin from '@/mixins/mixin-view-entity'

import { queryParamsSerializer, deserializeSorts, deserializeFilters } from '@/store/utils'
import { formatNumberTo3SD } from '@/utils'

import TemplateView from '@/templates/TemplateView.vue'
import UiFilters from '@/components/ui/filter/UiFilters.vue'
import UiExpandingFilters from '@/components/ui/filter/UiExpandingFilters.vue'
import ViewHeader from '@/components/app/ViewHeader.vue'

import ViewCasesList from '@/views/cases/ViewCasesList.vue'
import ViewCasesTable from '@/views/cases/ViewCasesTable.vue'
import ViewCasesSidebar from '@/views/cases/ViewCasesSidebar.vue'

import { UiSidebarWrapper } from '@hubsolv/hubsolv-ui'

@Component({
    components: {
        TemplateView,
        UiFilters,
        UiExpandingFilters,
        UiSidebarWrapper,
        ViewCasesList,
        ViewCasesTable,
        ViewCasesSidebar,
        ViewHeader,
    },
})
export default class ViewCases extends Mixins<EntityViewMixin<T.Case>>(EntityViewMixin) {

    ////////////////////////////////////////////////////////////////
    // Vuex bindings
    ////////////////////////////////////////////////////////////////
    
    @S.Cases.Getter all!: T.Case[]
    @S.Cases.Getter filters!: {[key: string]: string}
    @S.Cases.Getter hasNext!: boolean
    @S.Cases.Getter loading!: boolean
    @S.Cases.Getter totalResults!: number

    @S.Cases.Getter sorts!: {[key:string]: boolean}
    @S.Cases.Getter isAscending!: any

    @S.Cases.Action applySort!: any
    @S.Cases.Action clearFilters!: any
    @S.Cases.Action loadMore!: any
    @S.Cases.Action refresh!: any
    @S.Cases.Action removeFilter!: any
    @S.Cases.Action setFilter!: any

    @S.Cases.Mutation setFilters!: any
    @S.Cases.Mutation setSorts!: any

    ////////////////////////////////////////////////////////////////
    // API
    ////////////////////////////////////////////////////////////////
    
    timeoutId: number | null = null
    highlightedInput: null | keyof T.Case = null
    selectionLimit: number = 10

    showSidebar: boolean = false
    showFilters: boolean = false
    showCasesModal: boolean = false

    ////////////////////////////////////////////////////////////////
    // computed » view
    ////////////////////////////////////////////////////////////////

    get entitiesToDisplay () { return this.all }

    get totalItemCount () { return formatNumberTo3SD(this.totalResults || 0) }

    get query () { return this.$route.query }

    get itemsExist () { return this.entitiesToDisplay && this.entitiesToDisplay.length > 0 }

    get filtersExist () { return this.filters && !_isEmpty(this.filters) }

    get support () {
        return {
            email: config.SUPPORT.SUPPORT_EMAIL_ADDRESS,
        }
    }

    ////////////////////////////////////////////////////////////////
    // computed » properties
    ////////////////////////////////////////////////////////////////

    get properties () {
        const propertyDefinitions = C.CasePropertyDefinitions
        if (!propertyDefinitions) return null

        const orderedPropertyKeys: (keyof T.Case)[] = [
            'id',
            'name',
            'files',
            'createdAt',
            'updatedAt',
        ]

        const propertiesToDisplay: T.PropertyDefinition<T.Case>[] = []

        orderedPropertyKeys.forEach((key) => {
            const definition = propertyDefinitions[key]
            if (definition) propertiesToDisplay.push(definition)
        })

        return propertiesToDisplay
    }

    get filterableProperties () {
        if (!this.properties) return []

        return this.properties.filter((property) => {
            return property.isFilterable()
        })
    }

    get highlightFilterIcon () {
        if (!this.highlightedInput) return false
        return !this.filters.hasOwnProperty(this.highlightedInput)
    }

    ////////////////////////////////////////////////////////////////
    // methods
    ////////////////////////////////////////////////////////////////

    refreshEntities () {
        this.selected = []

        const serializedQuery = queryParamsSerializer({sort: this.sorts, ...this.filters})
        this.$router.push({path: `${this.$route.path}?${serializedQuery}`})
    }

    // Override mixin
    onCancel () {}
    
    ////////////////////////////////////////////////////////////////
    // event handlers
    ////////////////////////////////////////////////////////////////

    @Watch('query', {immediate: true})
    onQueryUpdate () {
        const {sort, ...filters} = this.query

        if (sort) this.setSorts( deserializeSorts(sort) )
        if (filters) this.setFilters( deserializeFilters(filters) )
        this.refresh()
    }
    
    onSetFilter (payload: { key: keyof T.Case, value: string | boolean, immediate?: boolean }) {
        const { key, value, immediate } = payload

        if (typeof value === 'string' && value.length === 0) {
            this.removeFilter({ key })
        } else {
            this.setFilter({ key, value })
        }
        
        if (typeof value === 'boolean' || immediate || value.length === 0) {
            this.refreshEntities()
            return
        }

        // Don't send a filter unless its more than 2 chars OR it's empty
        if (value.length > 1 || value.length === 0) {
            // Clear previous timeout.
            if (this.timeoutId !== null) {
                clearTimeout(this.timeoutId)
            }
            // Set up new timeout.
            this.timeoutId = setTimeout(() => {
                this.refreshEntities()
            }, config.UI.TEXT_INPUT_DEBOUNCE_IN_MILLISECONDS)
        }
    }

    onAddFilter (payload: { key: keyof T.File }) {
        const { key } = payload
        this.setFilter({ key, value: null })
    }

    onRemoveFilter (payload: { key: keyof T.Case }) {
        const { key } = payload
        this.removeFilter({ key })
        this.refreshEntities()
    }

    onRemoveAllFilters () {
        this.clearFilters()
        this.refreshEntities()
    }

    onSort (sortKey: string) {
        const sortKeyObject = this.properties && this.properties.find(({ key }) => key === sortKey)
        if (!sortKeyObject) return

        const { defaultSortAscending } = sortKeyObject
        if (defaultSortAscending === undefined) return

        const currentAscending = this.isAscending(sortKey)

        this.applySort({ key: sortKey, value: !currentAscending })
        this.refreshEntities()
    }

    onLoadMoreCases () {
        if (!this.hasNext) return
        this.loadMore()
    }

    onHighlightInput (key: keyof T.Case | null) {
        this.highlightedInput = key
    }

}
